import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import logger from '../utils/logger';
import { useCredits } from './CreditsContext';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const { spend } = useCredits();
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const audioRef = useRef(new Audio());

  // Callback to pause radio (will be set by RadioProvider)
  const radioPauseCallback = useRef(null);
  const radioResumeCallback = useRef(null);

  // Register radio callbacks
  const registerRadioCallbacks = useCallback((pauseFn, resumeFn) => {
    radioPauseCallback.current = pauseFn;
    radioResumeCallback.current = resumeFn;
  }, []);

  const playTrack = useCallback(async (track, tracks = []) => {
    logger.debug('🎵 PlayerContext.playTrack called with:', {
      title: track?.title,
      hasAudioUrl: 'audio_url' in (track || {}),
      audioUrl: track?.audio_url?.substring(0, 50),
      trackKeys: Object.keys(track || {})
    });

    // Credit-gated streaming: if the user is logged in and this is a full track,
    // attempt to deduct credits. Returns owned_free / admin_free / charged on
    // success, or opens the buy-credits modal on insufficient funds.
    if (track?.id) {
      const spendResult = await spend('track_stream', track.id);
      // If the user isn't logged in, allow preview play (server gates the
      // full audio URL separately based on access tier).
      if (spendResult && spendResult.ok === false && spendResult.insufficient) {
        // Modal already opened by spend(); don't start playback.
        return;
      }
    }

    // Pause radio when user plays a track
    if (radioPauseCallback.current) {
      radioPauseCallback.current();
    }

    setCurrentTrack(track);
    if (tracks.length > 0) {
      setQueue(tracks);
      const idx = tracks.findIndex(t => t.id === track.id);
      setQueueIndex(idx >= 0 ? idx : 0);
    }
    setIsPlaying(true);
  }, [spend]);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
    
    // Resume radio when user pauses their track
    if (radioResumeCallback.current && !currentTrack) {
      radioResumeCallback.current();
    }
  }, [currentTrack]);

  const nextTrack = useCallback(() => {
    if (queue.length > 0 && queueIndex < queue.length - 1) {
      const nextIdx = queueIndex + 1;
      setQueueIndex(nextIdx);
      setCurrentTrack(queue[nextIdx]);
      setIsPlaying(true);
    } else {
      // End of queue - resume radio
      setCurrentTrack(null);
      setIsPlaying(false);
      if (radioResumeCallback.current) {
        radioResumeCallback.current();
      }
    }
  }, [queue, queueIndex]);

  const prevTrack = useCallback(() => {
    if (queue.length > 0 && queueIndex > 0) {
      const prevIdx = queueIndex - 1;
      setQueueIndex(prevIdx);
      setCurrentTrack(queue[prevIdx]);
      setIsPlaying(true);
    }
  }, [queue, queueIndex]);

  const seek = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const updateVolume = useCallback((vol) => {
    setVolume(vol);
  }, []);

  const updateProgress = useCallback((time, dur) => {
    setCurrentTime(time);
    if (dur) setDuration(dur);
  }, []);

  // Split memoization into logical groups for better performance
  const playerState = React.useMemo(() => ({
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    volume,
    queue,
    queueIndex,
  }), [currentTrack, isPlaying, duration, currentTime, volume, queue, queueIndex]);

  const playerActions = React.useMemo(() => ({
    playTrack,
    togglePlay,
    pause,
    nextTrack,
    prevTrack,
    seek,
    updateVolume,
    updateProgress,
    registerRadioCallbacks,
  }), [playTrack, togglePlay, pause, nextTrack, prevTrack, seek, updateVolume, updateProgress, registerRadioCallbacks]);

  const value = React.useMemo(() => ({
    ...playerState,
    ...playerActions,
    audioRef
  }), [playerState, playerActions, audioRef]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};
